import axios from 'axios';
import { nanoid } from 'nanoid';

import { env } from 'env';

import { IParticipant, ParticipantState } from '../interfaces/participants';
import {
  IAddParticipantAvatarData,
  IDeleteParticipantAvatarData,
  IProfile,
  IProfileState,
  IConfigs,
  IUpdateParticipantData,
  IWsConnection,
} from '../interfaces/profile';
import { IParticipantEvents } from '../interfaces/event';
import { IAvatarLimited, IUserAvatar } from '@interfaces/avatar';

export default class ParticipantsService {
  static getParticipantRoom(participantId: string): Promise<{
    participantId: string;
    roomId: string;
  }> {
    return axios
      .get(`${env.REACT_APP_API_URL}api/v2/participants/${participantId}`)
      .then((res) => res.data);
  }

  static getEmptyParticipant(participantId: string): IParticipant {
    return {
      participantId,
      streamChatMemberId: participantId.replaceAll(/\|/gi, '_'),
      key: nanoid(10),
      email: '',
      name: '',
      firstName: '',
      lastName: '',
      fullName: '',
      avatarString: '',
      avatarImage: '',
      speaking: false,
      eventId: null,
      gameSessionId: null,
      roomId: null,
      region: null,
      isSpeaker: false,
      regions: [],
      state: ParticipantState.Loading,
      customAvatarUrl: '',
    };
  }

  static getConfigs(): Promise<IConfigs> {
    return axios
      .get(`${env.REACT_APP_API_URL}api/v1/profile/configs`)
      .then((res) => res.data);
  }

  static getWsConnections(): Promise<IWsConnection[]> {
    return axios
      .get(`${env.REACT_APP_API_URL}api/v1/profile/ws-connections`)
      .then((res) => res.data);
  }

  static async addParticipantAvatarToList(
    data: IAddParticipantAvatarData,
  ): Promise<IUserAvatar[]> {
    const response = await axios.post(
      `${env.REACT_APP_API_URL}api/v1/profile/avatars`,
      data,
    );

    return response.data;
  }

  static async getParticipantAvatars(): Promise<IUserAvatar[]> {
    return axios
      .get(`${env.REACT_APP_API_URL}api/v1/profile/avatars`)
      .then((res) => res.data);
  }

  static async deleteParticipantAvatarFromList(
    data: IDeleteParticipantAvatarData,
  ): Promise<IUserAvatar[]> {
    const response = await axios.delete(
      `${env.REACT_APP_API_URL}api/v1/profile/avatars`,
      { data },
    );

    return response.data;
  }

  static getCurrentParticipantProfile(): Promise<IProfile> {
    return axios
      .get(`${env.REACT_APP_API_URL}api/v1/profile`)
      .then((res) => res.data);
  }

  static getParticipantAvaturnAvatars(): Promise<IAvatarLimited[]> {
    return axios
      .get(`${env.REACT_APP_API_URL}api/v1/profile/avaturn/avatars`)
      .then((res) => res.data);
  }

  static async updateCurrentParticipantAvatar(
    data: IUpdateParticipantData,
  ): Promise<IProfile> {
    const response = await axios.patch(
      `${env.REACT_APP_API_URL}api/v1/profile/avatar`,
      data,
    );

    return response.data;
  }

  static updateCurrentParticipantProfileTC(
    data: IUpdateParticipantData,
  ): Promise<IProfile> {
    return axios.patch(`${env.REACT_APP_API_URL}api/v1/profile/tcs`, data);
  }

  static updateCurrentParticipantProfileState(
    data: IProfileState,
  ): Promise<IProfile> {
    return axios.patch(`${env.REACT_APP_API_URL}api/v1/profile/state`, data);
  }

  static getCurrentParticipantEvents(): Promise<IParticipantEvents> {
    return axios
      .get(`${env.REACT_APP_API_URL}api/v1/profile/events`)
      .then((res) => res.data);
  }

  static generateScreenshotUrls(fileName: string) {
    return axios
      .post(`${env.REACT_APP_API_URL}api/v1/profile/screenshot-urls`, {
        fileName,
      })
      .then((res) => res.data);
  }
}
